import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import {
  ConfirmDialog,
  Dialog,
  DialogFooter,
  DialogHeader,
  Dropdown,
  ErrorState,
  ForbiddenState,
  FormField,
  StateMessage,
} from "@/ds";
import { DominanciaBloco } from "../dominancia-bloco";
import { JustificativaField } from "@/shared/components";
import { FecharTooltip, fecharPt } from "../fechar-tooltip";
import {
  CANCELAR_LABEL,
  CARREGANDO_LABEL,
  NOVO_TIPO_TITLE,
  SEM_CENTRO_CUSTO_ELEGIVEL,
  TIPO_OBRA_LABEL,
} from "./novo-tipo-obra-dialog.constants";
import modal from "../planejamento-controle.modal.module.scss";
import styles from "./novo-tipo-obra-dialog.module.scss";
import type { NovoTipoObraDialogViewModel, OpcaoSelecao } from "./types-novo-tipo-obra-dialog";

const ROTULO = { label: modal.rotuloCampo };
const PAINEL = { panel: { className: modal.painel } };
const FECHAR_TESTID = "novo-tipo-fechar-x";
const DIALOGO_PT = { footer: { className: modal.dialogoRodape }, ...fecharPt(FECHAR_TESTID) };

function itemTipo(opcao: OpcaoSelecao) {
  return (
    <span className={styles.itemComTag}>
      {opcao.label}
      {opcao.tag ? (
        <Tag value={opcao.tag} severity="warning" rounded icon="pi pi-hourglass" />
      ) : null}
    </span>
  );
}

export function NovoTipoObraDialogView({
  formId,
  estado,
  errorMessage,
  recarregar,
  aviso,
  confirmarLabel,
  centroCusto,
  tipoObra,
  decisao,
  justificativa,
  isSubmitting,
  submitError,
  adicionarDesabilitado,
  adicionarMotivo,
  escolherCentroCusto,
  escolherTipoObra,
  digitarJustificativa,
  enviar,
  cancelar,
  descarte,
}: NovoTipoObraDialogViewModel) {
  return (
    <>
      <Dialog
        visible
        size="md"
        className={styles.dialogo}
        contentClassName={modal.corpo}
        onHide={cancelar}
        locked={isSubmitting}
        focusOnShow={false}
        header={<DialogHeader title={NOVO_TIPO_TITLE} />}
        pt={DIALOGO_PT}
        footer={
          <DialogFooter
            className={modal.rodape}
            cancelLabel={CANCELAR_LABEL}
            confirmLabel={confirmarLabel}
            confirmButtonProps={{
              disabled: adicionarDesabilitado,
              tooltip: adicionarMotivo ?? undefined,
            }}
            formId={formId}
            loading={isSubmitting}
            locked={isSubmitting}
            onCancel={cancelar}
            testId="novo-tipo"
          />
        }
        data-testid="novo-tipo-obra-dialog"
      >
        <FecharTooltip testId={FECHAR_TESTID} />
        {estado === "carregando" ? (
          <StateMessage variant="loading" title={CARREGANDO_LABEL} testId="novo-tipo-loading" />
        ) : null}
        {estado === "proibido" ? (
          <ForbiddenState description={errorMessage ?? undefined} testId="novo-tipo-error" />
        ) : null}
        {estado === "erro" ? (
          <ErrorState
            message={errorMessage ?? undefined}
            onRetry={recarregar}
            testId="novo-tipo-error"
          />
        ) : null}

        {estado === "pronto" || estado === "sem-centro-custo" ? (
          <form id={formId} className={styles.form} onSubmit={enviar} noValidate>
            {estado === "sem-centro-custo" ? (
              <Message
                className="p-message-block"
                severity="warn"
                text={SEM_CENTRO_CUSTO_ELEGIVEL}
                data-testid="novo-tipo-sem-centro-custo"
              />
            ) : null}
            <FormField.Root
              required
              classNames={ROTULO}
              error={centroCusto.error}
              help={centroCusto.ajuda ?? undefined}
              disabled={isSubmitting || centroCusto.travado}
            >
              <FormField.Label>{centroCusto.label}</FormField.Label>
              <FormField.Control>
                <Dropdown
                  appendTo="self"
                  pt={PAINEL}
                  value={centroCusto.value}
                  options={centroCusto.grupos.length > 0 ? centroCusto.grupos : centroCusto.opcoes}
                  optionGroupLabel={centroCusto.grupos.length > 0 ? "label" : undefined}
                  optionGroupChildren={centroCusto.grupos.length > 0 ? "items" : undefined}
                  placeholder={centroCusto.placeholder}
                  onChange={(event) => escolherCentroCusto(event.value)}
                  data-testid="novo-tipo-centro-custo"
                />
              </FormField.Control>
              <FormField.Help />
              <FormField.Error data-testid="novo-tipo-centro-custo-error" />
            </FormField.Root>

            <FormField.Root
              required
              classNames={ROTULO}
              error={tipoObra.error}
              disabled={isSubmitting || tipoObra.desabilitado}
            >
              <FormField.Label>{TIPO_OBRA_LABEL}</FormField.Label>
              <FormField.Control>
                <Dropdown
                  appendTo="self"
                  pt={PAINEL}
                  value={tipoObra.value}
                  options={tipoObra.opcoes}
                  optionDisabled="disabled"
                  itemTemplate={itemTipo}
                  placeholder={tipoObra.placeholder}
                  onChange={(event) => escolherTipoObra(event.value)}
                  data-testid="novo-tipo-tipo-obra"
                />
              </FormField.Control>
              <FormField.Error data-testid="novo-tipo-tipo-obra-error" />
            </FormField.Root>
            {tipoObra.semOpcao ? (
              <Message
                className="p-message-block"
                severity="warn"
                text={tipoObra.semOpcao}
                data-testid="novo-tipo-sem-tipo"
              />
            ) : null}

            {aviso ? (
              <Message
                className="p-message-block"
                severity="info"
                text={aviso}
                data-testid="novo-tipo-aviso-gmud"
              />
            ) : null}

            {decisao ? <DominanciaBloco {...decisao} /> : null}

            {justificativa ? (
              <JustificativaField
                labelClassName={modal.rotuloCampo}
                value={justificativa.value}
                onChange={digitarJustificativa}
                error={justificativa.error}
                disabled={isSubmitting}
                rows={3}
                testIds={{
                  campo: "novo-tipo-justificativa",
                  contador: "novo-tipo-contador",
                  erro: "novo-tipo-justificativa-error",
                }}
              />
            ) : null}

            {submitError ? (
              <Message
                className="p-message-block"
                severity="error"
                text={submitError}
                data-testid="novo-tipo-submit-error"
              />
            ) : null}
          </form>
        ) : null}
      </Dialog>

      {descarte ? <ConfirmDialog {...descarte} /> : null}
    </>
  );
}
